'use client';

import * as React from 'react';
import { RadioGroup } from '@base-ui/react/radio-group';
import { Radio } from '@base-ui/react/radio';
import { Plus, TriangleAlert } from 'lucide-react';
import { clsx } from 'clsx';
import { Avatar } from './Avatar';
import { Button } from './Button';
import styles from './CandidateSelectList.module.css';

export interface CandidateSelectListItem {
  id: string | number;
  name: string;
  avatar?: string;
  /** Secondary line shown under the name, e.g. the candidate's position title */
  subtitle?: string;
  /** Tooltip text shown next to the item when something needs attention (e.g. a deleted profile) */
  warning?: string;
}

export interface CandidateSelectListProps {
  items: CandidateSelectListItem[];
  value?: string | number | null;
  onValueChange?: (value: string | number) => void;
  onRemove?: (id: string | number) => void;
  onAdd?: () => void;
  addLabel?: string;
  className?: string;
}

export const CandidateSelectList = React.forwardRef<HTMLDivElement, CandidateSelectListProps>(
  ({ items, value, onValueChange, onRemove, onAdd, addLabel = 'Add another candidate', className }, ref) => {
    const labelIdPrefix = React.useId();

    return (
      <div ref={ref} className={clsx(styles.root, className)}>
        <RadioGroup value={value} onValueChange={onValueChange} className={styles.group}>
          {items.map((item) => {
            const nameId = `${labelIdPrefix}-${item.id}`;
            return (
              <div key={item.id} className={styles.itemWrapper}>
                {/* Remove sits outside Radio.Root on purpose: focus events
                    bubble, and Radio.Root's onFocus clicks its hidden input
                    once the group has been arrow-keyed, so a nested control
                    would silently change the selection when tabbed to. The
                    card chrome therefore lives on the row, not the radio, so
                    Remove can sit inside it. */}
                <div
                  className={styles.itemRow}
                  data-checked={value === item.id ? '' : undefined}
                >
                  {/* The avatar is a sibling of the radio (it spans both rows),
                      so it forwards clicks to keep the whole card selectable. */}
                  <span
                    className={styles.avatarCell}
                    onClick={() => onValueChange?.(item.id)}
                  >
                    <Avatar src={item.avatar} name={item.name} size="md" variant="rounded" />
                  </span>
                  <Radio.Root
                    value={item.id}
                    className={styles.item}
                    aria-labelledby={nameId}
                  >
                    <span className={styles.itemBody}>
                      <span className={styles.itemName} id={nameId}>{item.name}</span>
                      {item.subtitle && (
                        <span className={styles.itemSubtitle}>{item.subtitle}</span>
                      )}
                    </span>
                    {item.warning && (
                      <span className={styles.warning} title={item.warning}>
                        <TriangleAlert size={16} />
                      </span>
                    )}
                  </Radio.Root>
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => onRemove?.(item.id)}
                  >
                    Remove
                  </button>
                </div>
                <div className={styles.divider} />
              </div>
            );
          })}
        </RadioGroup>

        {onAdd && (
          <div className={styles.itemWrapper}>
            <Button
              variant="secondary"
              size="sm"
              fullWidth
              startIcon={<Plus size={16} />}
              onClick={onAdd}
              className={styles.addButton}
            >
              {addLabel}
            </Button>
          </div>
        )}
      </div>
    );
  }
);

CandidateSelectList.displayName = 'CandidateSelectList';

export default CandidateSelectList;
